import { twMerge } from 'tailwind-merge';

import { useIsomorphicLayoutEffect } from './hooks/useIsomorphicLayoutEffect';

/**
 * Top safe-area ownership for the native iOS shell (#2028).
 *
 * The shell paints edge-to-edge (`viewport-fit=cover`, capacitor `contentInset:
 * 'never'`), so the status-bar / Dynamic Island band is page territory. The
 * shared page header (renderer/Header.tsx) fills it with a spacer inside its
 * opaque bar, and so do the other inset-bearing top chromes (marketing
 * header/banner, AuthShell, the support-chat conversation bar). Any view that
 * reaches the layout's <main> with NO such owner (a Header-less page like the
 * order page for signed-in users, the editor, a suspense/skeleton fallback)
 * would otherwise start at y=0 under the status bar.
 *
 * Owners register through `useSafeTopOwner`; while at least one is mounted,
 * <main> carries `data-safe-top-owned` and the layout adds nothing. Otherwise
 * <main> pads its top by the inset and paints an opaque status-bar backdrop —
 * see the `main[data-url]:not([data-safe-top-owned])` rules in
 * renderer/styles/index.css. The attribute is toggled imperatively (module
 * counter + layout effect, no React state) so it never re-renders the layout,
 * and it is set before paint, so a page whose header mounts after a loading
 * state doesn't flash a double inset.
 *
 * Why not a pure-CSS `main:not(:has([data-safe-top]))`? WKWebView (iOS 26)
 * left `main` with a stale padding after the owner mounted — `matches()`
 * reported the selector as no longer matching while the computed padding stayed
 * at the inset — so the header pages rendered a double inset. Toggling an
 * attribute on <main> itself invalidates reliably.
 *
 * env() is 0 on web / Android / non-notch devices, so every rule here is inert
 * there (0px padding, 0px-tall backdrop, 0px spacer).
 */

export const SAFE_TOP_OWNED_ATTR = 'data-safe-top-owned';

let owners = 0;

function syncOwnedAttribute() {
  if (typeof document === 'undefined') {
    return;
  }
  for (const main of document.querySelectorAll('main[data-url]')) {
    main.toggleAttribute(SAFE_TOP_OWNED_ATTR, owners > 0);
  }
}

/** Marks the calling component as carrying the top safe-area inset itself. */
export function useSafeTopOwner(active = true) {
  useIsomorphicLayoutEffect(() => {
    if (!active) {
      return;
    }
    owners += 1;
    syncOwnedAttribute();
    return () => {
      owners -= 1;
      syncOwnedAttribute();
    };
  }, [active]);
}

/** Renders nothing; registers its parent chrome as the inset owner. */
export function SafeTopOwner() {
  useSafeTopOwner();
  return null;
}

/**
 * The status-bar spacer for an opaque top bar: a block the height of the inset
 * placed INSIDE the bar (a spacer, not padding, so callers' `py-*` overrides
 * can't drop it), which also registers the bar as the inset owner.
 */
export function SafeTopSpacer({ className }: { className?: string }) {
  useSafeTopOwner();
  return <div aria-hidden className={twMerge('h-[env(safe-area-inset-top,0px)]', className)} />;
}
